import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Shield, Save, Loader2, AlertTriangle, ExternalLink, History, ChevronDown, ChevronRight, CheckCircle2, XCircle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import FneStickerWidget from '../../components/admin/FneStickerWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_META = {
  FNE_INVOICE_CERTIFIED: { label: 'Facture certifiée', color: 'text-emerald-700 bg-emerald-50 border-emerald-300', icon: CheckCircle2 },
  FNE_INVOICE_REFUNDED: { label: 'Avoir émis', color: 'text-slate-700 bg-slate-50 border-slate-300', icon: XCircle },
  FNE_API_ERROR: { label: 'Erreur API', color: 'text-rose-700 bg-rose-50 border-rose-300', icon: AlertTriangle },
  FNE_SETTINGS_UPDATED: { label: 'Config modifiée', color: 'text-blue-700 bg-blue-50 border-blue-300', icon: Save },
  FNE_ENVIRONMENT_SWITCHED: { label: 'Environnement basculé', color: 'text-amber-700 bg-amber-50 border-amber-300', icon: AlertTriangle },
};

function FneAuditRow({ log }) {
  const [expanded, setExpanded] = useState(false);
  const meta = ACTION_META[log.action] || { label: log.action, color: 'text-slate-700 bg-slate-50 border-slate-300', icon: History };
  const Icon = meta.icon;
  const details = log.details || {};
  const hasDetails = Object.keys(details).length > 0;
  const isError = log.action === 'FNE_API_ERROR';

  return (
    <div
      className={`border rounded-md text-sm transition-colors ${isError ? 'border-rose-200 bg-rose-50/50' : 'border-slate-200'}`}
      data-testid={`fne-audit-row-${log.action}`}
    >
      <button
        type="button"
        onClick={() => hasDetails && setExpanded(e => !e)}
        className={`w-full flex items-start justify-between p-3 gap-3 ${hasDetails ? 'hover:bg-slate-50 cursor-pointer' : ''}`}
      >
        <div className="flex items-start gap-2 flex-1 min-w-0">
          {hasDetails ? (
            expanded ? <ChevronDown className="h-4 w-4 mt-0.5 text-slate-400" /> : <ChevronRight className="h-4 w-4 mt-0.5 text-slate-400" />
          ) : <div className="w-4" />}
          <div className={`p-1 rounded ${meta.color.split(' ')[1]}`}>
            <Icon className={`h-3.5 w-3.5 ${meta.color.split(' ')[0]}`} />
          </div>
          <div className="text-left flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-semibold ${meta.color.split(' ')[0]}`}>{meta.label}</span>
              <span className="text-[10px] font-mono text-slate-400">{log.action}</span>
              {details.mock && <Badge variant="secondary" className="text-[10px]">MOCK</Badge>}
              {details.environment && (
                <Badge variant="outline" className="text-[10px]">{details.environment.toUpperCase()}</Badge>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {log.actorEmail} • {log.targetType}#{(log.targetId || '').slice(0, 8)}
            </div>
            {details.reference && (
              <div className="text-xs text-slate-700 mt-0.5">
                Réf FNE : <span className="font-mono">{details.reference}</span>
              </div>
            )}
            {(details.message || details.code) && (
              <div className="text-xs text-rose-700 mt-0.5 truncate">
                {details.code && <span className="font-mono">[{details.code}]</span>} {details.message}
              </div>
            )}
          </div>
        </div>
        <div className="text-[11px] text-slate-400 whitespace-nowrap flex-shrink-0">
          {log.createdAt ? new Date(log.createdAt).toLocaleString('fr-FR') : ''}
        </div>
      </button>
      {expanded && hasDetails && (
        <div className="border-t bg-slate-50 p-3 text-xs space-y-2" data-testid={`fne-audit-details-${log.targetId}`}>
          {details.context && (
            <div>
              <span className="font-semibold text-slate-700">Contexte : </span>
              <span className="font-mono">{details.context}</span>
            </div>
          )}
          {details.changed_fields && details.changed_fields.length > 0 && (
            <div>
              <span className="font-semibold text-slate-700">Champs modifiés : </span>
              <span className="font-mono">{details.changed_fields.join(', ')}</span>
            </div>
          )}
          {details.cleared_fields && details.cleared_fields.length > 0 && (
            <div>
              <span className="font-semibold text-slate-700">Champs effacés : </span>
              <span className="font-mono text-rose-700">{details.cleared_fields.join(', ')}</span>
            </div>
          )}
          {details.reason && (
            <div>
              <span className="font-semibold text-slate-700">Motif : </span>
              {details.reason}
            </div>
          )}
          <div>
            <span className="font-semibold text-slate-700">Détails complets :</span>
            <pre className="mt-1 p-2 bg-white border rounded text-[11px] overflow-x-auto whitespace-pre-wrap break-words max-h-64">
              {JSON.stringify(details, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

const FIELDS = [
  { key: 'ncc', label: 'NCC Baobab Connect', placeholder: 'Numéro Compte Contribuable (10 chiffres)', sensitive: false },
  { key: 'taxRegime', label: "Régime d'imposition (CGI)", type: 'select',
    hint: "La TVA ne peut être collectée qu'en RNI ou RSI. En RME, TEE ou TCE, les factures sont émises sans TVA (exonération légale TVAD). Tout changement informe automatiquement par écrit l'équipe commerciale (notification + email) et s'applique aux NOUVEAUX devis uniquement.",
    options: [
      { value: 'RNI', label: "RNI — Réel normal (CA > 500 M, TVA mensuelle)" },
      { value: 'RSI', label: 'RSI — Réel simplifié (CA 200–500 M, TVA trimestrielle)' },
      { value: 'RME', label: 'RME — Microentreprises (CA 50–200 M, sans TVA)' },
      { value: 'TEE', label: "TEE — Taxe d'État de l'entreprenant (CA ≤ 50 M, sans TVA)" },
      { value: 'TCE', label: "TCE — Taxe communale de l'entreprenant (CA ≤ 50 M, sans TVA)" },
    ] },
  { key: 'ntd', label: 'NTD Baobab Connect', placeholder: 'Numéro Télédéclarant', sensitive: false },
  { key: 'pointOfSale', label: 'Point de vente', placeholder: 'ex. Treichville', sensitive: false },
  { key: 'establishment', label: 'Établissement', placeholder: 'ex. Baobab Connect HQ', sensitive: false },
  { key: 'tokenDev', label: 'Token JWT — DEV', placeholder: 'Bearer token espace FNE DEV', sensitive: true },
  { key: 'tokenProd', label: 'Token JWT — PROD', placeholder: 'Bearer token espace FNE PROD', sensitive: true },
  { key: 'apiBaseUrlDev', label: 'URL API DEV', placeholder: 'http://54.247.95.108', sensitive: false },
  { key: 'apiBaseUrlProd', label: 'URL API PROD', placeholder: 'Fourni par la DGI après validation', sensitive: false },
  { key: 'stickerThreshold', label: 'Seuil alerte stickers', placeholder: '100', type: 'number' },
  { key: 'stickerCritical', label: 'Seuil critique stickers', placeholder: '20', type: 'number' },
  { key: 'financeServiceEmail', label: 'Email service financier', placeholder: 'finance@baobabco.net', sensitive: false },
  { key: 'comptabilityEmail', label: 'Email comptabilité (alertes FNE)', placeholder: 'compta@baobabco.net', sensitive: false },
  { key: 'certStartDateDev', label: 'Date début certification — DEV', type: 'date', placeholder: 'Ex: 2026-02-07', hint: 'Toutes les opérations antérieures seront marquées "Hors périmètre FNE" en DEV' },
  { key: 'certStartDateProd', label: 'Date début certification — PROD', type: 'date', placeholder: 'Ex: 2026-03-01', hint: 'À définir au moment du basculement en PROD officiel DGI' },
  { key: 'invoiceFooter', label: 'Footer FNE (mentions légales facture)', type: 'textarea', placeholder: 'BAOBAB CONNECT (SARL) | RCCM : CI-ABJ-... | Email : compta@... – Tél : +225 ...', hint: 'Injecté dans le payload DGI et imprimé en pied de facture FNE.' },
  { key: 'sandboxDailyLimit', label: 'Quota sandbox DEV (certifs/jour)', type: 'number', placeholder: '0 = aucune limite', hint: 'Protection anti-gaspillage stickers en sandbox DEV uniquement. 0 ou vide = aucune limite. Ne s applique jamais en PROD.' },
];

export default function FneSettings() {
  const { toast } = useToast();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [switchConfirm, setSwitchConfirm] = useState('');
  const [switching, setSwitching] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        axios.get(`${API}/fne/settings`),
        axios.get(`${API}/fne/audit-log?limit=50`),
      ]);
      // Snapshot des valeurs sauvegardées (préfixées __saved_) pour afficher "💾 Sauvegardé : X"
      // séparément du champ en cours d'édition.
      const snapshot = {};
      for (const k of Object.keys(s.data || {})) {
        snapshot[`__saved_${k}`] = s.data[k];
      }
      setSettings({ ...s.data, ...snapshot });
      setAuditLog(a.data.items || []);
    } catch (err) {
      toast({
        title: 'Erreur chargement FNE',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleChange = (key, value) => {
    setSettings(s => ({ ...s, [key]: value }));
  };

  const handleClearField = async (key) => {
    setSaving(true);
    try {
      // Reset l'état local immédiatement (avant le PATCH) pour feedback UI
      setSettings(s => ({ ...s, [key]: '' }));
      await axios.patch(`${API}/fne/settings`, { [key]: '__CLEAR__' });
      toast({ title: `Champ "${key}" effacé` });
      await load();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const patch = {};
      for (const f of FIELDS) {
        const v = settings[f.key];
        const saved = settings[`__saved_${f.key}`];

        // Pour les dates : autoriser la mise à null (effacement)
        if (f.type === 'date') {
          if ((v === '' || v === null || v === undefined) && (saved !== '' && saved !== null && saved !== undefined)) {
            patch[f.key] = null;
            continue;
          }
          if (v && String(v).slice(0, 10) !== String(saved ?? '').slice(0, 10)) {
            patch[f.key] = String(v).slice(0, 10);  // YYYY-MM-DD parsé par Pydantic comme datetime
            continue;
          }
          continue;
        }

        if (v === '' || v === null || v === undefined) continue;
        if (typeof v === 'string' && v === '***masked***') continue;
        // N'envoyer que si différent du dernier état sauvegardé (évite réécriture inutile)
        if (String(v) === String(saved ?? '')) continue;
        patch[f.key] = f.type === 'number' ? Number(v) : v;
      }
      // mockMode et autoCertify : envoyer seulement si modifiés
      if (settings.mockMode !== settings.__saved_mockMode) patch.mockMode = !!settings.mockMode;
      if (settings.autoCertify !== settings.__saved_autoCertify) patch.autoCertify = !!settings.autoCertify;
      if (settings.hiddenTaxEnabled !== settings.__saved_hiddenTaxEnabled) patch.hiddenTaxEnabled = !!settings.hiddenTaxEnabled;
      if (Object.keys(patch).length === 0) {
        toast({ title: 'Aucun changement à enregistrer' });
        setSaving(false);
        return;
      }
      const updated = await axios.patch(`${API}/fne/settings`, patch);
      toast({
        title: 'Paramètres FNE sauvegardés',
        description: `Champs mis à jour : ${Object.keys(patch).join(', ')}`,
      });
      // Refresh snapshot via load() pour que les badges "non sauvegardé" disparaissent
      await load();
      return updated;
    } catch (err) {
      toast({
        title: 'Erreur sauvegarde',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSwitchEnv = async () => {
    if (!settings) return;
    const targetEnv = settings.environment === 'dev' ? 'prod' : 'dev';
    if (targetEnv === 'prod' && switchConfirm !== 'BASCULER PROD') {
      toast({
        title: 'Confirmation incorrecte',
        description: "Tapez exactement 'BASCULER PROD' pour basculer en production",
        variant: 'destructive',
      });
      return;
    }
    setSwitching(true);
    try {
      await axios.post(`${API}/fne/settings/switch-env`, {
        environment: targetEnv,
        confirmation: targetEnv === 'prod' ? 'BASCULER PROD' : 'switch-to-dev',
      });
      toast({ title: `Bascule réussie vers ${targetEnv.toUpperCase()}` });
      setSwitchOpen(false);
      setSwitchConfirm('');
      await load();
    } catch (err) {
      toast({
        title: 'Erreur bascule',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSwitching(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!settings) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>Impossible de charger les paramètres FNE</AlertDescription>
      </Alert>
    );
  }

  const isProd = settings.environment === 'prod';

  return (
    <div className="space-y-6" data-testid="fne-settings-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-emerald-600" />
            Paramètres FNE — DGI Côte d'Ivoire
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Configuration de la certification électronique des factures.
          </p>
        </div>
        <a
          href="https://fne.dgi.gouv.ci/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-emerald-700 hover:text-emerald-900 flex items-center gap-1"
        >
          Portail FNE <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      {/* Status banner */}
      <Alert
        className={isProd ? 'border-rose-300 bg-rose-50' : 'border-amber-300 bg-amber-50'}
        data-testid="fne-env-status-banner"
      >
        <AlertTriangle className={`h-4 w-4 ${isProd ? 'text-rose-700' : 'text-amber-700'}`} />
        <AlertTitle>
          Environnement actif : <strong>{settings.environment.toUpperCase()}</strong>
          {settings.mockMode && <Badge variant="secondary" className="ml-2">MODE MOCK</Badge>}
          {settings.autoCertify && <Badge className="ml-2 bg-emerald-600">Auto-cert activée</Badge>}
        </AlertTitle>
        <AlertDescription>
          {settings.mockMode
            ? "Le mode mock est actif : aucun appel réseau réel vers la DGI. Désactivez-le une fois les tokens configurés."
            : isProd
              ? "🔴 PRODUCTION ACTIVE — chaque certification consomme un sticker réel."
              : "Mode DEV : appels réels vers l'API DGI de test (http://54.247.95.108)."}
        </AlertDescription>
      </Alert>

      {/* Widget stickers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FneStickerWidget />
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Bascule d'environnement</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-500">
              Bascule entre DEV (tests) et PROD (factures réelles). La bascule PROD nécessite une confirmation textuelle.
            </p>
            <Button
              variant={isProd ? 'outline' : 'destructive'}
              onClick={() => setSwitchOpen(true)}
              data-testid="fne-switch-env-btn"
              className="w-full"
            >
              {isProd ? 'Revenir en DEV' : 'Basculer en PROD'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Toggles */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Comportement global</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Mode MOCK</Label>
              <p className="text-xs text-slate-500">Simule les réponses FNE sans appel réseau.</p>
            </div>
            <Switch
              checked={!!settings.mockMode}
              onCheckedChange={(v) => handleChange('mockMode', v)}
              data-testid="fne-mock-toggle"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Certification automatique</Label>
              <p className="text-xs text-slate-500">
                Certifie automatiquement chaque facture lors du passage en statut "envoyée".
              </p>
            </div>
            <Switch
              checked={!!settings.autoCertify}
              onCheckedChange={(v) => handleChange('autoCertify', v)}
              data-testid="fne-autocert-toggle"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Taxe globale 2 % (incorporée aux prix)</Label>
              <p className="text-xs text-slate-500">
                Majore de 2 % le prix unitaire des nouveaux devis pour couvrir les frais
                d&apos;encaissement, sans ligne visible pour le client. Reste désactivable
                opération par opération depuis le devis.
              </p>
            </div>
            <Switch
              checked={!!settings.hiddenTaxEnabled}
              onCheckedChange={(v) => handleChange('hiddenTaxEnabled', v)}
              data-testid="fne-hidden-tax-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Champs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Identifiants & API</CardTitle>
          <CardDescription>
            Données fournies par la DGI après inscription sur fne.dgi.gouv.ci
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FIELDS.map((f) => {
              const isFilled = settings[f.key] && settings[f.key] !== '';
              // Snapshot de la dernière valeur sauvegardée (utile pour comparer pendant la saisie)
              const savedValue = settings[`__saved_${f.key}`] !== undefined ? settings[`__saved_${f.key}`] : settings[f.key];
              const currentValue = settings[f.key] ?? '';
              const isDirty = !f.sensitive && currentValue !== '' && String(currentValue) !== String(savedValue ?? '');
              return (
                <div key={f.key} className={`space-y-1.5 ${f.type === 'textarea' ? 'md:col-span-2' : ''}`}>
                  <Label htmlFor={`fne-field-${f.key}`} className="text-sm flex items-center justify-between gap-2">
                    <span>{f.label}</span>
                    {isDirty && <span className="text-[10px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded">non sauvegardé</span>}
                  </Label>
                  <div className="flex gap-2">
                    {f.type === 'select' ? (
                      <select
                        id={`fne-field-${f.key}`}
                        value={settings[f.key] ?? ''}
                        onChange={(e) => handleChange(f.key, e.target.value)}
                        className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        data-testid={`fne-input-${f.key}`}
                      >
                        <option value="">— Non configuré —</option>
                        {(f.options || []).map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    ) : f.type === 'textarea' ? (
                      <textarea
                        id={`fne-field-${f.key}`}
                        value={settings[f.key] ?? ''}
                        onChange={(e) => handleChange(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        rows={3}
                        className="w-full min-h-[78px] rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        data-testid={`fne-input-${f.key}`}
                      />
                    ) : (
                      <Input
                        id={`fne-field-${f.key}`}
                        type={
                          f.type === 'number' ? 'number'
                            : f.type === 'date' ? 'date'
                            : (f.sensitive ? 'password' : 'text')
                        }
                        value={
                          f.type === 'date' && settings[f.key]
                            ? String(settings[f.key]).slice(0, 10)
                            : (settings[f.key] ?? '')
                        }
                        onChange={(e) => handleChange(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        data-testid={`fne-input-${f.key}`}
                      />
                    )}
                    {f.sensitive && isFilled && settings[f.key] !== '***masked***' && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleClearField(f.key)}
                        disabled={saving}
                        data-testid={`fne-clear-${f.key}`}
                        title={`Effacer ${f.label}`}
                      >
                        Effacer
                      </Button>
                    )}
                    {f.type === 'date' && isFilled && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleChange(f.key, '')}
                        disabled={saving}
                        data-testid={`fne-clear-${f.key}`}
                        title="Effacer la date"
                      >
                        Effacer
                      </Button>
                    )}
                  </div>
                  {f.hint && (
                    <p className="text-[11px] text-slate-500 italic">{f.hint}</p>
                  )}
                  {f.sensitive && settings[f.key] === '***masked***' && (
                    <p className="text-[11px] text-slate-400">
                      Token déjà configuré (caché). Saisissez une nouvelle valeur pour l'écraser, ou cliquez Effacer.
                    </p>
                  )}
                  {!f.sensitive && savedValue !== null && savedValue !== undefined && savedValue !== '' && (
                    <p className="text-[10px] text-slate-500" data-testid={`fne-saved-${f.key}`}>
                      💾 Sauvegardé : <span className="font-mono text-slate-700">{String(savedValue)}</span>
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex justify-end mt-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="fne-save-btn"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Save className="h-4 w-4 mr-2" />
              Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Audit log */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <History className="h-4 w-4" />
            Historique des actions FNE (50 dernières)
          </CardTitle>
          <Button variant="outline" size="sm" onClick={load}>
            Rafraîchir
          </Button>
        </CardHeader>
        <CardContent>
          {auditLog.length === 0 ? (
            <p className="text-sm text-slate-500">Aucune action FNE enregistrée pour le moment.</p>
          ) : (
            <div className="space-y-1" data-testid="fne-audit-log">
              {auditLog.map((log, idx) => <FneAuditRow key={idx} log={log} />)}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Switch env modal */}
      <Dialog open={switchOpen} onOpenChange={setSwitchOpen}>
        <DialogContent data-testid="fne-switch-modal">
          <DialogHeader>
            <DialogTitle>
              Bascule vers {settings.environment === 'dev' ? 'PRODUCTION' : 'DEV'}
            </DialogTitle>
            <DialogDescription>
              {settings.environment === 'dev' ? (
                <>
                  Vous allez activer la <strong className="text-rose-700">PRODUCTION FNE</strong>.
                  Chaque facture certifiée consommera un sticker réel et sera transmise à la DGI.
                  <br /><br />
                  Tapez <strong className="font-mono">BASCULER PROD</strong> pour confirmer.
                </>
              ) : (
                "Vous allez revenir en mode DEV (tests). Les factures déjà certifiées en PROD restent inchangées."
              )}
            </DialogDescription>
          </DialogHeader>
          {settings.environment === 'dev' && (
            <Input
              value={switchConfirm}
              onChange={(e) => setSwitchConfirm(e.target.value)}
              placeholder="BASCULER PROD"
              data-testid="fne-switch-confirm-input"
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSwitchOpen(false)} disabled={switching}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleSwitchEnv}
              disabled={switching}
              data-testid="fne-switch-confirm-btn"
            >
              {switching && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmer la bascule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
